import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, Legend } from "recharts";
import { BarChart3, Users, Clock, Wrench, TrendingUp, Download } from "lucide-react";
import * as XLSX from "xlsx";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658', '#ff7300'];

export default function ProjectReport({ site, onClose }) {
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());

  const fetchReport = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/sites/${site.id}/report?year=${year}&month=${month}`);
      if (!response.ok) throw new Error("Eroare la încărcare");
      const data = await response.json();
      setReportData(data);
    } catch (error) {
      toast.error("Eroare la încărcarea raportului");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (site?.id) {
      fetchReport();
    }
  }, [site?.id, month, year]);

  const exportToExcel = () => {
    if (!reportData) return;
    
    const wb = XLSX.utils.book_new();
    
    // Summary sheet
    const summaryData = [
      ['RAPORT PROIECT', ''],
      ['Șantier:', reportData.site?.name || ''],
      ['Adresă:', reportData.site?.address || ''],
      ['Perioada:', reportData.period?.month_name || ''],
      ['', ''],
      ['SUMAR', ''],
      ['Total Ore Lucrate:', reportData.summary?.total_hours || 0],
      ['Angajați Unici:', reportData.summary?.unique_workers || 0],
      ['Zile Lucrate Total:', reportData.summary?.total_work_days || 0],
      ['Media Ore/Zi:', reportData.summary?.avg_hours_per_day || 0],
    ];
    const ws1 = XLSX.utils.aoa_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(wb, ws1, 'Sumar');
    
    // Workers sheet
    const workersData = [
      ['ANGAJAȚI', '', ''],
      ['Nume', 'Ore Lucrate', 'Zile Lucrate'],
      ...(reportData.workers || []).map(w => [w.user_name, w.total_hours, w.days_worked])
    ];
    const ws2 = XLSX.utils.aoa_to_sheet(workersData);
    XLSX.utils.book_append_sheet(wb, ws2, 'Angajați');
    
    // Tools sheet
    const toolsData = [
      ['UNELTE UTILIZATE', '', ''],
      ['Unealtă', 'Ore Utilizare', 'Nr. Utilizări'],
      ...(reportData.tool_usage || []).map(t => [t.name, t.total_hours, t.usage_count])
    ];
    const ws3 = XLSX.utils.aoa_to_sheet(toolsData);
    XLSX.utils.book_append_sheet(wb, ws3, 'Unelte');
    
    // Daily hours sheet
    const dailyData = [
      ['ORE PE ZI', ''],
      ['Data', 'Ore'],
      ...(reportData.daily_hours || []).map(d => [d.date, d.hours])
    ];
    const ws4 = XLSX.utils.aoa_to_sheet(dailyData);
    XLSX.utils.book_append_sheet(wb, ws4, 'Ore Zilnice');
    
    const siteName = reportData.site?.name?.replace(/\s/g, '_') || 'proiect';
    const periodName = reportData.period?.month_name?.replace(/\s/g, '_') || 'raport';
    XLSX.writeFile(wb, `raport_${siteName}_${periodName}.xlsx`);
    toast.success("Raport Excel generat!");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  if (!reportData || !reportData.site) {
    return (
      <div className="text-center py-8 text-slate-500">
        Nu s-au putut încărca datele raportului.
      </div>
    );
  }

  const workerChartData = (reportData.workers || []).slice(0, 8).map(w => ({
    name: w.user_name?.split(' ')[0] || 'Unknown',
    ore: w.total_hours || 0,
    zile: w.days_worked || 0
  }));

  const toolChartData = (reportData.tool_usage || []).slice(0, 6).map((t, i) => ({
    name: t.name || 'Unknown',
    value: t.total_hours || 0,
    color: COLORS[i % COLORS.length]
  }));

  const summary = reportData.summary || {};
  const dailyHours = reportData.daily_hours || [];
  const workers = reportData.workers || [];
  const subcontractors = reportData.subcontractors || [];

  return (
    <div className="space-y-6 max-h-[80vh] overflow-y-auto p-1">
      {/* Header with filters */}
      <div className="flex flex-wrap items-center justify-between gap-4 sticky top-0 bg-white z-10 pb-4 border-b">
        <div>
          <h2 className="text-2xl font-bold" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
            {reportData.site.name}
          </h2>
          <p className="text-slate-600">{reportData.site.address}</p>
        </div>
        
        <div className="flex gap-2 items-center">
          <Select value={month.toString()} onValueChange={(v) => setMonth(parseInt(v))}>
            <SelectTrigger className="w-32">
              <SelectValue placeholder="Luna" />
            </SelectTrigger>
            <SelectContent>
              {[...Array(12)].map((_, i) => (
                <SelectItem key={i + 1} value={(i + 1).toString()}>
                  {new Date(2000, i).toLocaleString('ro-RO', { month: 'long' })}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Select value={year.toString()} onValueChange={(v) => setYear(parseInt(v))}>
            <SelectTrigger className="w-24">
              <SelectValue placeholder="An" />
            </SelectTrigger>
            <SelectContent>
              {[2024, 2025, 2026].map(y => (
                <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Button onClick={exportToExcel} variant="outline" className="gap-2">
            <Download className="w-4 h-4" />
            Excel
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white border-0">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Clock className="w-8 h-8 opacity-80" />
              <div>
                <p className="text-2xl font-bold">{summary.total_hours || 0}h</p>
                <p className="text-sm opacity-80">Total Ore</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white border-0">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Users className="w-8 h-8 opacity-80" />
              <div>
                <p className="text-2xl font-bold">{summary.unique_workers || 0}</p>
                <p className="text-sm opacity-80">Angajați</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white border-0">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <TrendingUp className="w-8 h-8 opacity-80" />
              <div>
                <p className="text-2xl font-bold">{summary.total_work_days || 0}</p>
                <p className="text-sm opacity-80">Zile Lucrate</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-orange-500 to-orange-600 text-white border-0">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <BarChart3 className="w-8 h-8 opacity-80" />
              <div>
                <p className="text-2xl font-bold">{summary.avg_hours_per_day || 0}h</p>
                <p className="text-sm opacity-80">Media/Zi</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Workers Bar Chart */}
        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-600" />
              Ore per Angajat
            </CardTitle>
          </CardHeader>
          <CardContent>
            {workerChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={workerChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Bar dataKey="ore" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-center text-slate-500 py-8">Nu există date</p>
            )}
          </CardContent>
        </Card>

        {/* Tools Pie Chart */}
        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Wrench className="w-5 h-5 text-green-600" />
              Utilizare Unelte
            </CardTitle>
          </CardHeader>
          <CardContent>
            {toolChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={toolChartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                    label={({ name, value }) => `${name}: ${value}h`}
                  >
                    {toolChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-center text-slate-500 py-8">Nu există date</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Daily Hours Line Chart */}
      <Card className="border-0 shadow-lg">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-purple-600" />
            Evoluția Orelor pe Zile
          </CardTitle>
        </CardHeader>
        <CardContent>
          {dailyHours.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={dailyHours}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="date" 
                  tick={{ fontSize: 10 }}
                  tickFormatter={(v) => v?.split('-')[2] || ''}
                />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip labelFormatter={(v) => `Data: ${v}`} />
                <Line 
                  type="monotone" 
                  dataKey="hours" 
                  stroke="#8b5cf6" 
                  strokeWidth={2}
                  dot={{ fill: '#8b5cf6', r: 4 }}
                  name="Ore"
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-center text-slate-500 py-8">Nu există date pentru această perioadă</p>
          )}
        </CardContent>
      </Card>

      {/* Workers Table */}
      <Card className="border-0 shadow-lg">
        <CardHeader>
          <CardTitle className="text-lg">Detalii Angajați</CardTitle>
        </CardHeader>
        <CardContent>
          {workers.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-slate-50">
                    <th className="text-left p-3 font-semibold">Angajat</th>
                    <th className="text-center p-3 font-semibold">Zile</th>
                    <th className="text-center p-3 font-semibold">Ore Total</th>
                    <th className="text-center p-3 font-semibold">Media Ore/Zi</th>
                  </tr>
                </thead>
                <tbody>
                  {workers.map((worker, idx) => (
                    <tr key={worker.user_id} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                      <td className="p-3 font-medium">{worker.user_name}</td>
                      <td className="p-3 text-center">
                        <Badge variant="outline">{worker.days_worked}</Badge>
                      </td>
                      <td className="p-3 text-center font-semibold text-blue-600">
                        {(worker.total_hours || 0).toFixed(1)}h
                      </td>
                      <td className="p-3 text-center text-slate-600">
                        {worker.days_worked > 0 ? ((worker.total_hours || 0) / worker.days_worked).toFixed(1) : 0}h
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-center text-slate-500 py-8">Nu există angajați cu prezențe în această perioadă</p>
          )}
        </CardContent>
      </Card>

      {/* Subcontractors if any */}
      {subcontractors.length > 0 && (
        <Card className="border-0 shadow-lg border-l-4 border-l-orange-500">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="w-5 h-5 text-orange-600" />
              Subcontractori Asignați
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {subcontractors.map(sub => (
                <div key={sub.id} className="p-3 bg-orange-50 rounded-lg">
                  <p className="font-semibold">{sub.name}</p>
                  <p className="text-sm text-slate-600">
                    {sub.contact_person && `Contact: ${sub.contact_person} • `}
                    {sub.employees?.length || 0} angajați
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
